import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Rocket, RefreshCw, Flame } from 'lucide-react';
import Navigation from '@/components/Navigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const dayLabel = (dateStr, idx) => {
  if (idx === 0) return 'Today';
  if (idx === 1) return 'Yesterday';
  return dateStr;
};

const HRTracker = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/mlb/homers`, { withCredentials: true });
      setDays(res.data.days || []);
    } catch (e) {
      console.error('homers error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const goToPlayer = (hr) => {
    if (hr.team) navigate(`/matchups?team=${encodeURIComponent(hr.team)}`);
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Rocket className="h-8 w-8 text-brand-lime" />
                Home Run Tracker
              </h1>
              <p className="text-muted-foreground mt-1">Every MLB home run — distance, exit velocity, and pitch details</p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-homers-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-48 skeleton rounded-lg" />)}</div>
          ) : days.length === 0 ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No home run data available.</CardContent></Card>
          ) : (
            <div className="space-y-8">
              {days.map((day, dIdx) => (
                <Card key={day.date || dIdx} className="bg-[#0f1e36] border-[#1e3a5f]" data-testid={`homers-day-${dIdx}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-heading text-xl font-bold text-white uppercase">
                        {dayLabel(day.date, dIdx)}
                        <span className="text-muted-foreground text-sm font-normal normal-case ml-2">{day.date}</span>
                      </div>
                      <Badge variant="outline" className="border-brand-lime text-brand-lime">
                        {day.total} HR{day.total === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {day.homers.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No home runs yet.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-muted-foreground text-[10px] uppercase border-b border-[#1e3a5f]">
                              <th className="text-left py-2 px-2">Team</th>
                              <th className="text-left py-2 px-2">Batter</th>
                              <th className="text-center py-2 px-2" title="Season HR total">HR #</th>
                              <th className="text-center py-2 px-2" title="Runners on base">RoB</th>
                              <th className="text-center py-2 px-2">Inn</th>
                              <th className="text-center py-2 px-2">Outs</th>
                              <th className="text-center py-2 px-2">LA°</th>
                              <th className="text-center py-2 px-2">EV</th>
                              <th className="text-center py-2 px-2">Dist</th>
                              <th className="text-left py-2 px-2">Pitch</th>
                              <th className="text-left py-2 px-2">Pitcher</th>
                            </tr>
                          </thead>
                          <tbody>
                            {day.homers.map((hr, i) => {
                              const moonshot = hr.distance >= 425;
                              return (
                                <tr key={i} className={`border-b border-[#1e3a5f]/40 table-row-hover ${moonshot ? 'bg-brand-lime/5' : ''}`} data-testid={`hr-row-${dIdx}-${i}`}>
                                  <td className="py-2 px-2"><span className="sport-icon sport-mlb text-[9px] px-1.5 py-0.5">{hr.team}</span></td>
                                  <td className="py-2 px-2">
                                    <button onClick={() => goToPlayer(hr)} className="text-white font-medium hover:text-brand-cyan transition-colors flex items-center gap-1" data-testid={`hr-batter-${dIdx}-${i}`}>
                                      {moonshot && <Flame className="h-3 w-3 text-brand-lime" />}
                                      {hr.batter_name}
                                    </button>
                                  </td>
                                  <td className="text-center font-mono text-brand-cyan font-bold">{hr.season_total ?? '—'}</td>
                                  <td className="text-center text-muted-foreground">{hr.runners_on ?? 0}</td>
                                  <td className="text-center text-muted-foreground">{hr.inning ?? '—'}</td>
                                  <td className="text-center text-muted-foreground">{hr.outs ?? '—'}</td>
                                  <td className="text-center font-mono text-white">{hr.launch_angle ?? '—'}</td>
                                  <td className="text-center font-mono text-white">{hr.exit_velocity ?? '—'}</td>
                                  <td className={`text-center font-mono font-bold ${moonshot ? 'text-brand-lime' : 'text-white'}`}>{hr.distance ?? '—'}</td>
                                  <td className="py-2 px-2 text-muted-foreground">{hr.pitch}</td>
                                  <td className="py-2 px-2 text-muted-foreground">{hr.pitcher}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default HRTracker;
